import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin } from 'rxjs';

import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { OrdemProducaoOrquestradorService } from '../../../../../shared/services/apis/orquestrador-suprimentos/ordem-producao-orquestrador.service';
import { OrdemProducaoListStruct } from '../../../../../shared/services/structs/suprimentos/ordem-producao-list.struct';
import { OrdemProducaoDetalheStruct } from '../../../../../shared/services/structs/suprimentos/ordem-producao-detalhe.struct';
import { StatusOrdemProducaoEnum } from '../../../../../shared/enums/suprimentos/status-ordem-producao.enum';
import { LancarEstoqueOrdemProducaoItemRequest } from '../../../../../shared/services/requests/suprimentos/lancar-estoque-ordem-producao-item.request';

export interface LancarEstoqueLoteOrdemProducaoDialogData {
  ordens: OrdemProducaoListStruct[];
}

interface OrdemExcluidaLote {
  numero: number;
  nomeProduto: string;
  motivo: string;
}

interface InsumoConsolidadoRow {
  idProduto: number;
  nomeProduto: string;
  unidade: string;
  isFracionada: boolean;
  quantidadeTotal: number;
  idLocalizacaoEstoque: number | null;
}

/**
 * Lançamento de estoque em lote (seção 5.3 da US#141200) — consolida os insumos de várias OPs por
 * SKU num único combo de localização de saída por SKU, e um combo único de localização de entrada
 * para todos os produtos fabricados do lote. Sem ajuste fino de quantidade e sem lote/série (OPs
 * assim são excluídas, exigem lançamento individual). Ao confirmar, decompõe de volta por OP e
 * chama o MESMO endpoint do lançamento individual, só que com N itens em `ordens`.
 */
@Component({
  selector: 'app-lancar-estoque-lote-ordem-producao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    AbntPipe,
    AlertCardComponent,
  ],
  templateUrl: './lancar-estoque-lote-ordem-producao-dialog.component.html',
  styleUrl: './lancar-estoque-lote-ordem-producao-dialog.component.css',
})
export class LancarEstoqueLoteOrdemProducaoDialogComponent implements OnInit {
  public isCarregando = true;
  public isSalvando = false;

  public localizacoes: any[] = [];
  public idLocalizacaoEstoqueEntrada: number | null = null;

  public ordensElegiveis: OrdemProducaoDetalheStruct[] = [];
  public ordensExcluidas: OrdemExcluidaLote[] = [];
  public insumosConsolidados: InsumoConsolidadoRow[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LancarEstoqueLoteOrdemProducaoDialogData,
    public dialogRef: MatDialogRef<LancarEstoqueLoteOrdemProducaoDialogComponent>,
    private alertService: AlertService,
    private lookupService: LookupService,
    private ordemProducaoOrquestradorService: OrdemProducaoOrquestradorService,
  ) {}

  public ngOnInit(): void {
    const localizacoes$ = this.lookupService.getLocalizacaoEstoqueSelect();
    const ordens$ = forkJoin(this.data.ordens.map((o) => this.ordemProducaoOrquestradorService.getById(o.idOrdemProducao)));

    forkJoin([localizacoes$, ordens$]).subscribe({
      next: ([localizacaoResponse, respostas]) => {
        this.isCarregando = false;

        // O backend já devolve o local marcado como padrão em primeiro (OrderByDescending
        // isPadrao) — pré-selecionar aqui, sem precisar de um campo isPadrao na struct do lookup.
        // Usuário pode trocar livremente em qualquer um dos selects (entrada e cada insumo).
        if (!localizacaoResponse?.isError) this.localizacoes = localizacaoResponse.list ?? [];
        this.idLocalizacaoEstoqueEntrada = this.localizacoes[0]?.id ?? null;

        for (const res of respostas) {
          if (res.isError || !res.ordemProducao) continue;
          const ordem = res.ordemProducao;

          // OPs já lançadas e canceladas: ignoradas silenciosamente (seção 5.3 do handoff).
          if (ordem.isEstoqueLancado || ordem.idStatusOrdemProducao === StatusOrdemProducaoEnum.Cancelada) continue;

          const temRastreabilidade = ordem.isControlaLotes || ordem.isControlaSeries
            || ordem.itens.some((i) => i.isControlaLotes || i.isControlaSeries);
          if (temRastreabilidade) {
            this.ordensExcluidas.push({
              numero: ordem.numero,
              nomeProduto: ordem.nomeProduto,
              motivo: 'produto ou insumo com controle de lote/série',
            });
            continue;
          }

          this.ordensElegiveis.push(ordem);
        }

        this.consolidarInsumos();
      },
      error: (err) => {
        this.isCarregando = false;
        this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
      },
    });
  }

  private consolidarInsumos(): void {
    const idPadrao = this.localizacoes[0]?.id ?? null;
    const mapa = new Map<number, InsumoConsolidadoRow>();
    for (const ordem of this.ordensElegiveis) {
      for (const item of ordem.itens) {
        const existente = mapa.get(item.idProduto);
        if (existente) {
          existente.quantidadeTotal += item.quantidadeTotal;
        } else {
          mapa.set(item.idProduto, {
            idProduto: item.idProduto,
            nomeProduto: item.nomeProduto,
            unidade: item.unidade,
            isFracionada: item.isUnidadeMedidaFracionada,
            quantidadeTotal: item.quantidadeTotal,
            idLocalizacaoEstoque: idPadrao,
          });
        }
      }
    }
    this.insumosConsolidados = Array.from(mapa.values());
  }

  public get podeConfirmar(): boolean {
    if (this.isSalvando || this.ordensElegiveis.length === 0) return false;
    if (!this.idLocalizacaoEstoqueEntrada) return false;
    return this.insumosConsolidados.every((i) => !!i.idLocalizacaoEstoque);
  }

  public cancelar(): void {
    if (!this.isSalvando) this.dialogRef.close();
  }

  public confirmar(): void {
    if (!this.podeConfirmar) return;

    const localizacaoPorInsumo = new Map(this.insumosConsolidados.map((i) => [i.idProduto, i.idLocalizacaoEstoque!]));

    const ordensRequest: LancarEstoqueOrdemProducaoItemRequest[] = this.ordensElegiveis.map((ordem) => ({
      idOrdemProducao: ordem.idOrdemProducao,
      idLocalizacaoEstoqueEntrada: this.idLocalizacaoEstoqueEntrada!,
      quantidadeEntrada: ordem.quantidade,
      justificativaAjusteEntrada: null,
      insumos: ordem.itens.map((item) => ({
        idOrdemProducaoItem: item.idOrdemProducaoItem,
        idLocalizacaoEstoque: localizacaoPorInsumo.get(item.idProduto)!,
        lotes: [],
        series: [],
      })),
      codigoLote: null,
      dataFabricacaoLote: null,
      dataValidadeLote: null,
      codigosSerie: [],
    }));

    this.isSalvando = true;
    this.ordemProducaoOrquestradorService.lancarEstoque({ ordens: ordensRequest }).subscribe({
      next: (res) => {
        this.isSalvando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', `Estoque lançado para ${this.ordensElegiveis.length} ordem(ns).`, AlertType.success);
        if (res.avisoDescricao) this.alertService.show('Atenção', res.avisoDescricao, AlertType.warning);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isSalvando = false;
        this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
      },
    });
  }
}
